// src/components/map/hooks/adapters/useMapLifecycle.ts
//
// Adapter hook: Map Lifecycle
//
// Owns:
//   - createMapbox() (map instance creation with auth, controls, error handling)
//   - loadImages() (facility icon loading)
//   - addMapboxLayers() (source/layer/draw-control initialization)
//   - initializeMap() (RxJS subscription for debounced move/zoom events)
//   - Main mount/unmount useEffect (style.load → waiting → layers → filtering)
//   - resetCursor(), onMapMove()
//   - state ref (extentChanged/isIdle/styledata)
//   - styleDataHandler, idleHandler, moveHandler, errorHandler, clearSelectedData
//
// Mapbox methods called: new Map(), addControl, removeControl, addSource, addLayer,
//                        on/off for style.load, idle, styledata, move, error, etc.
//
// This hook was extracted from MapboxWrapper as part of the concern-based
// decomposition (Phase 8). It is the last to extract because all other hooks
// must be stable first — the lifecycle hook coordinates them during the
// style.load → waiting → addMapboxLayers → filterWellOnMap pipeline.
import * as Sentry from "@sentry/react";
import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { IS_DEV_ENV } from "constants/app.constants";
import {
  FACILITY_LABEL_LAYER,
  SELECTED_WELL_LAYER,
  SELECTED_WELL_POINT_LAYER,
  WELL_LABEL_LAYER
} from "constants/mapLayers.constants";
import _debounce from "lodash/debounce";
import mapboxgl, { GeoJSONSource, LngLatLike } from "mapbox-gl";
import FreeDraw from "mapbox-gl-draw-freehand-mode";
import { fromEvent, merge } from "rxjs";
import { debounceTime, map as maprx } from "rxjs/operators";
import { PadScenario } from "store/features";
import { setMapIdleTime } from "store/features/dataTable/dataTableSlice";
import { selectMapState, useMapStore } from "stores/map";
import { useDebouncedCallback } from "use-debounce";
import { publicUrl } from "utils/publicUrl";

import useBetaFeatures from "hooks/useBetaFeatures";
import useFeatureFlags from "hooks/useFeatureFlags";

import { API_REQUEST_HEADERS, isSameOrigin } from "api/apiRequestHeaders";
import { displayUnitFor } from "api/displayUnitHeader";

import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, DEFAULT_ZOOM } from "models/mapDefaults";

import View3DControl from "../../View3DControl";
import { BaseLayerKind, EntitiesInGroupModel } from "../../contexts/MapContextState";
import { getMapLayerSources, getMapLayers } from "../../hooks/getMapLayers";
import DrawCircle from "../../modes/DrawCircle";
import DrawRectangle from "../../modes/DrawRectangle";
import { BaseWellStyle } from "../../utils/baseWellStyle";
import { handleBaseWellTileError } from "../../utils/baseWellTileErrors";
import { polygonStyles } from "../../utils/drawStyles";
import { emptyFeatureCollection } from "../../utils/emptyFeatureCollection";
import { getBaseLayerStyleUrl } from "../../utils/getBaseLayerStyleUrl";
import { buildMapExtentPolygon } from "../../utils/mapExtentPolygon";
import { setMapInstance } from "../../utils/mapInstanceRegistry";
import { TileUris } from "../../utils/mapboxHelper";
import DrawTwoPointLineString from "../../utils/two_point_line";
import { useMapDispatch } from "../useMapDispatch";
import { mapRequestHeaders, shouldAttachMapAuth } from "./mapTransformRequest";

// ── Constants ────────────────────────────────────────────────────────────────

const mapServiceEndpoint = process.env.REACT_APP_MAP_SERVICE;
const geoMapServiceEndpoint = process.env.REACT_APP_GEO_MAP_SERVICE;
const shapetileServiceEndpoint = process.env.REACT_APP_SHAPETILE_SERVICE;
const mapRsServiceEndpoint = process.env.REACT_APP_MAP_RS_SERVICE;

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * How long the camera must sit still before the map re-reads its viewport. The
 * tests that drive this pipeline advance timers by exactly this much.
 */
export const MOVE_DEBOUNCE_MS = 700;

export interface UseMapLifecycleParams {
  /** Ref to the Mapbox GL map instance (written by this hook). */
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
  /** Ref to the MapboxDraw instance. */
  readonly drawRef: MutableRefObject<MapboxDraw>;
  /** Dashboard widget ID — used as the map container. */
  readonly id: string;
  /** Ref tracking whether the component is still mounted. */
  readonly isMountedRef: MutableRefObject<boolean>;
  /** Map context dispatch function. */
  readonly mapDispatch: ReturnType<typeof useMapDispatch>;
  /** Layer add function from the shell. */
  readonly addLayer: (
    layer: mapboxgl.Layer & { before?: string },
    before?: string
  ) => void;
  /** Ref to the view-locked state. */
  readonly viewLockedRef: MutableRefObject<boolean>;
  /** Ref to the dashboard-locked state. */
  readonly viewLockedByDashboardRef: MutableRefObject<boolean>;
  /**
   * Ref to whether the viewport may currently drive the filter. Set while
   * composing a screenshot with quantile bins, whose boundaries are computed
   * from the well population; expanding the map, opening the geom-bin panel and
   * every other bin type must keep syncing the extent.
   */
  readonly extentSyncFrozenRef: MutableRefObject<boolean>;
  /** Ref to selected groups. */
  readonly selectedGroupsRef: MutableRefObject<string[]>;
  /** Clear attention wells immediately. From useMapAttentionOverlay. */
  readonly clearHoverHighlighting: () => void;
  /** Debounced clear attention wells. From useMapAttentionOverlay. */
  readonly clearHoverHighlightingDebounced: ReturnType<typeof useDebouncedCallback>;
  /** Filter wells on map. From useMapTileSourceManager. */
  readonly filterWellOnMap: (
    txn: { id: string; facilityId: string },
    groupEntities: Record<string, EntitiesInGroupModel>,
    facilityEntities: Record<string, EntitiesInGroupModel>,
    scenarios: PadScenario[]
  ) => void;
  /** Update tile URIs. From useMapTileSourceManager. */
  readonly updateWellsAndFacilitiesOnMap: (
    wellId: string,
    facilityId: string,
    scenarios: PadScenario[],
    facilityEntities?: Record<string, EntitiesInGroupModel>
  ) => void;
  /** Check whether on-map filtering is possible. From useMapTileSourceManager. */
  readonly canFilterWellsOnMap: (mapbox: mapboxgl.Map) => boolean;
  /** Label update function. From useLabelDensity. */
  readonly updateLabels: (id: string, layer: string) => void;
  /** Ref to current tile URIs. From useMapTileSourceManager. */
  readonly tileUriRef: MutableRefObject<TileUris>;
  /** Ref to current txnId. */
  readonly txnIdRef: MutableRefObject<{ id: string; facilityId: string }>;
  /** Ref to current pad scenarios (checked list). */
  readonly checkedPadScenariosRef: MutableRefObject<PadScenario[]>;
  /** Current entities in group (from MapContext). */
  readonly entitiesInGroup: Record<string, EntitiesInGroupModel>;
  /** Current facilities in group (from MapContext). */
  readonly facilitiesInGroup: Record<string, EntitiesInGroupModel>;
  /** Ref to auth token. */
  readonly tokenRef: MutableRefObject<string>;
  /** Whether user's default map settings have loaded. */
  readonly isDefaultMapSettingsLoaded: boolean;
  /** Organization ID. */
  readonly orgId: string;
  /** The base well layer's own filterId (EVA-35). No filterId → no layers/source. */
  readonly filterId: Maybe<string>;
  /** Fingerprint of the org's ordered survey preference — see buildSurveyPreferenceToken. */
  readonly prefHash: Maybe<string>;
  /** Fingerprint of the org's ordered header preference — see buildHeaderPreferenceToken. */
  readonly attrPrefHash: Maybe<string>;
  /** Resolved base well style, so a style reload re-adds layers already styled. */
  readonly baseWellStyle: BaseWellStyle;
  /** Saved Line Thickness, so a style reload re-adds well lines already at it. */
  readonly wellLineThickness: number;
  /** Debounced mouse move to cancel on unmount. From useMapHoverLegend. */
  readonly onMouseMoveDebounce: { cancel: () => void };
  /** Debounced mouse leave to cancel on unmount. From useMapHoverLegend. */
  readonly onMouseLeaveDebounce: { cancel: () => void };
  /** User's default base layer style, loaded from user settings. */
  readonly defaultBaseLayer?: BaseLayerKind;
  /** Gates the base well layer architecture (EVA-35). See getMapLayers. */
  readonly baseLayerRefactor?: boolean;
  /** Legacy-only inputs, forwarded to getMapLayers when baseLayerRefactor is false. */
  readonly hasSurvey?: boolean;
  readonly hasGdc?: boolean;
  readonly hasIhs?: boolean;
  readonly baseLayerResourcePlays?: readonly string[];
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/** Facility and activity icons: the file under public/static, its mapbox image id, and options (sdf by default). */
const MAP_ICONS: [
  file: string,
  name: string,
  options?: { sdf?: boolean; pixelRatio?: number }
][] = [
  ["factory-custom.png", "gas-plant", { sdf: true, pixelRatio: 0.5 }],
  ["compressor-station.png", "compressor-station"],
  ["battery.png", "battery"],
  ["injection-disposal-facility.png", "injection-facility"],
  ["gathering-system.png", "gathering-system"],
  ["meter-station.png", "metering-station"],
  ["waste-plant.png", "waste-plant"],
  ["default-facility.png", "default-facility"],
  ["type-log.png", "type-log"],
  ["rig-outlined.png", "rig", { sdf: true, pixelRatio: 4 }],
  ["frac-crew-outlined.png", "frac-crew", { sdf: true, pixelRatio: 4 }]
];

export function useMapLifecycle({
  mapboxRef,
  drawRef,
  id,
  isMountedRef,
  mapDispatch,
  addLayer,
  viewLockedRef,
  viewLockedByDashboardRef,
  extentSyncFrozenRef,
  selectedGroupsRef,
  clearHoverHighlighting,
  clearHoverHighlightingDebounced,
  filterWellOnMap,
  updateWellsAndFacilitiesOnMap,
  canFilterWellsOnMap,
  updateLabels,
  tileUriRef,
  txnIdRef,
  checkedPadScenariosRef,
  entitiesInGroup,
  facilitiesInGroup,
  tokenRef,
  isDefaultMapSettingsLoaded,
  orgId,
  filterId,
  prefHash,
  attrPrefHash,
  baseWellStyle,
  wellLineThickness,
  onMouseMoveDebounce,
  onMouseLeaveDebounce,
  defaultBaseLayer,
  baseLayerRefactor,
  hasSurvey,
  hasGdc,
  hasIhs,
  baseLayerResourcePlays
}: UseMapLifecycleParams): void {
  // ── Redux / Zustand ────────────────────────────────────────────────────
  const dispatch = useDispatch();
  const mapState = useMapStore(selectMapState);
  const { hasFeature } = useBetaFeatures();
  // Stable boolean so callbacks can depend on flag resolution without
  // re-creating on every render (hasFeature itself is a fresh closure).
  const hasRigFracLayers = hasFeature("Rig And Frac Layers");
  const { hasFeature: hasFeatureFlag } = useFeatureFlags();
  // EVA-10: hard prerequisite for FACILITY_LAYER/FACILITY_LABEL_LAYER (see
  // getMapLayers.ts). Read here rather than passing useBetaFeatures'
  // `hasFeature` through, since "Base Midstream" lives in the separate
  // FEATURE_FLAGS set (useFeatureFlags), not BETA_FEATURES.
  const hasBaseMidstream = hasFeatureFlag("Base Midstream");
  const setMapExtent = useMapStore((s) => s.setMapExtent);
  const setMapState = useMapStore((s) => s.setMapState);
  const beginMapMove = useMapStore((s) => s.beginMapMove);
  const endMapMove = useMapStore((s) => s.endMapMove);

  // ── Internal refs ──────────────────────────────────────────────────────
  const mapId = useRef(id);
  const state = useRef({
    extentChanged: false,
    isIdle: false,
    styledata: true
  });
  // Whether this map currently holds one of the store's moving-map counts.
  const isClaimingMove = useRef(false);

  // The style.load handler below is registered once (effect keyed on
  // isDefaultMapSettingsLoaded), so referencing these callbacks/values
  // directly would capture their mount-time versions. After the user toggles
  // group mode, the captured `updateWellsAndFacilitiesOnMap`/`canFilterWellsOnMap`
  // still see the old `mode`, so a base-layer switch re-applies non-group tiles —
  // wells lose their bin colour (render black) and facility icons drop. Keep the
  // latest versions in a ref so the handler always re-applies the current mode.
  const styleReloadRef = useRef({
    filterWellOnMap,
    updateWellsAndFacilitiesOnMap,
    canFilterWellsOnMap,
    updateLabels,
    entitiesInGroup,
    facilitiesInGroup
  });
  useEffect(() => {
    styleReloadRef.current = {
      filterWellOnMap,
      updateWellsAndFacilitiesOnMap,
      canFilterWellsOnMap,
      updateLabels,
      entitiesInGroup,
      facilitiesInGroup
    };
  });

  // ── resetCursor ────────────────────────────────────────────────────────

  const resetCursor = useCallback(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox) {
      return;
    }
    mapbox.getCanvas().style.cursor = "default";
  }, [mapboxRef]);

  // ── onMapMove ──────────────────────────────────────────────────────────

  const onMapMove = useCallback(
    (mapbox: mapboxgl.Map) => {
      const bounds = mapbox.getBounds();
      const polygon = buildMapExtentPolygon(bounds.getSouthWest(), bounds.getNorthEast());
      setMapExtent(polygon);
      state.current = { extentChanged: true, isIdle: false, styledata: false };
      resetCursor();
    },
    [setMapExtent, resetCursor]
  );

  // ── loadImages ─────────────────────────────────────────────────────────

  const loadImages = useCallback((mapbox: mapboxgl.Map) => {
    const loadImageSafely = (
      path: string,
      name: string,
      options: { sdf?: boolean; pixelRatio?: number } = { sdf: true }
    ) => {
      mapbox.loadImage(path, (error, image) => {
        if (error) {
          Sentry.withScope((scope) => {
            scope.setContext("map_image_loading", {
              imageName: name,
              imagePath: path,
              action: "loadImage",
              status: "failed"
            });
            scope.setLevel("error");
            Sentry.captureException(error);
          });
          return;
        }
        if (!mapbox.hasImage(name)) {
          mapbox.addImage(name, image, options);
        }
      });
    };

    MAP_ICONS.forEach(([file, name, options]) =>
      loadImageSafely(publicUrl(`static/${file}`), name, options)
    );
    return () => {
      isMountedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── createMapbox ───────────────────────────────────────────────────────

  const createMapbox = useCallback((): mapboxgl.Map => {
    // eslint-disable-next-line import/no-named-as-default-member
    mapboxgl.accessToken =
      "pk.eyJ1IjoiaG9kYTk5IiwiYSI6ImNrMWxhcngydDAzcXMzbXA3dGs3Ym5vMjYifQ.Aao1Lz2YhOUIpJLi2ZplHw";

    const center: LngLatLike = mapState
      ? [mapState.lng, mapState.lat]
      : [DEFAULT_LONGITUDE, DEFAULT_LATITUDE];
    const zoom = mapState ? mapState.zoom : DEFAULT_ZOOM;

    const baseLayerStyleUrl = getBaseLayerStyleUrl(defaultBaseLayer);

    // Initialize Map
    // eslint-disable-next-line import/no-named-as-default-member
    const mapbox = new mapboxgl.Map({
      container: mapId.current,
      style: baseLayerStyleUrl as mapboxgl.Style | string,
      center: center,
      zoom: zoom,
      preserveDrawingBuffer: true,
      transformRequest: (url, resourceType) => {
        if (
          shouldAttachMapAuth(url, {
            mapServiceEndpoint,
            geoMapServiceEndpoint,
            shapetileServiceEndpoint,
            mapRsServiceEndpoint
          })
        ) {
          return {
            url: url,
            // Tiles from a service that reads the unit (map-svc: IPDB and base-well; map-rs)
            // carry it exactly as REST calls to it do, so the map and the data agree.
            headers: mapRequestHeaders(url, tokenRef.current, displayUnitFor(url))
          };
        }
        // The service images (GeoTIFF) were matched above; the images left are the
        // app's own /static icons (map.loadImage), not API calls.
        if (resourceType !== "Image" && isSameOrigin(url)) {
          return { url, headers: API_REQUEST_HEADERS };
        }
      }
    }) as mapboxgl.Map;

    // Register map instance for access from components outside MapProvider
    setMapInstance(mapbox);

    // Expose map instance to window for debugging in development
    if (IS_DEV_ENV) {
      (window as unknown as { __mapboxInstance: mapboxgl.Map }).__mapboxInstance = mapbox;
      // eslint-disable-next-line no-console
      console.log("Mapbox instance exposed to window.__mapboxInstance for debugging");
    }

    // eslint-disable-next-line listeners/no-missing-remove-event-listener, listeners/no-inline-function-event-listener
    mapbox.on("load", () => {
      loadImages(mapbox);
    });

    // Handle raster tile loading errors gracefully
    mapbox.on("error", (e) => {
      // A not-ready base-well feed (eva-map-svc#594 answers 503 while its
      // attribute store loads) fails one tile at a time. Downgraded and
      // throttled in one shared place — see baseWellTileErrors.
      if (handleBaseWellTileError(e)) return;
      if (
        e.error?.message?.includes("Could not load image") ||
        e.error?.message?.includes("could not be decoded")
      ) {
        Sentry.withScope((scope) => {
          scope.setContext("map_raster_tile", {
            sourceId: e.sourceId,
            errorMessage: e.error?.message,
            action: "tile_load",
            status: "failed"
          });
          scope.setLevel("warning");
          Sentry.captureMessage(
            `Map raster tile failed to load from source "${e.sourceId}": ${e.error.message}`
          );
        });
        return;
      }
      Sentry.withScope((scope) => {
        scope.setContext("map_error", {
          errorType: e.error?.name,
          errorMessage: e.error?.message,
          sourceId: e.sourceId,
          action: "map_operation",
          status: "failed"
        });
        scope.setLevel("error");
        Sentry.captureException(e.error || e);
      });
    });

    // eslint-disable-next-line import/no-named-as-default-member
    mapbox.addControl(new mapboxgl.NavigationControl({ showZoom: false }));
    mapbox.addControl(new View3DControl(), "top-right");

    return mapbox;
  }, [mapState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── addMapboxLayers ────────────────────────────────────────────────────

  const addMapboxLayers = useCallback(() => {
    const mapbox = mapboxRef.current;

    if (!mapbox) return;
    const sources = getMapLayerSources();
    for (const sourceId of Object.keys(sources)) {
      if (mapbox.getSource(sourceId)) {
        continue;
      }
      mapbox.addSource(sourceId, sources[sourceId]);
    }

    const layers = getMapLayers({
      orgId,
      filterId,
      prefHash,
      attrPrefHash,
      baseWellStyle,
      wellLineThickness,
      hasFeature,
      hasBaseMidstream,
      baseLayerRefactor,
      hasSurvey,
      hasGdc,
      hasIhs,
      baseLayerResourcePlays
    });
    for (const layer of layers) {
      // Skip only the layer that is already there — never abandon the rest of
      // the pass. Another effect can legitimately win the race to add one of
      // these layers (e.g. MapboxWrapper's base-well effect after the style
      // finished loading), and a `return` here would then drop every layer
      // declared after it — grids, wells, sticks, facilities — leaving them
      // missing until the next style reload.
      if (mapbox.getLayer(layer.id)) {
        continue;
      }
      addLayer(layer, layer.before);
    }

    // Remove existing draw control if present (handles style reload case)
    if (drawRef.current) {
      try {
        mapbox.removeControl(drawRef.current);
      } catch (e) {
        // Control may not be on map, ignore error
      }
      drawRef.current = null;
    }

    // Add draw control after all the layers have been added
    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      userProperties: true,
      modes: Object.assign(MapboxDraw.modes, {
        draw_polygon: FreeDraw,
        two_point_line: DrawTwoPointLineString,
        draw_circle: DrawCircle,
        draw_rectangle: DrawRectangle
      }),
      styles: polygonStyles
    });

    try {
      mapbox.addControl(drawRef.current);
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setContext("mapbox_draw_control", {
          action: "addControl",
          status: "failed"
        });
        scope.setLevel("error");
        Sentry.captureException(error);
      });
    }
  }, [
    // hasRigFracLayers stands in for hasFeature (a fresh closure every render),
    // so the callback re-fires when flag resolution lands even if that ever
    // decouples from orgId loading.
    addLayer,
    drawRef,
    hasRigFracLayers,
    hasBaseMidstream,
    mapboxRef,
    orgId,
    filterId,
    prefHash,
    attrPrefHash,
    baseWellStyle,
    wellLineThickness,
    baseLayerRefactor,
    hasSurvey,
    hasGdc,
    hasIhs,
    baseLayerResourcePlays
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // style.load is registered once; read the latest callback, not the mount-time one.
  const addMapboxLayersRef = useRef(addMapboxLayers);
  useEffect(() => {
    addMapboxLayersRef.current = addMapboxLayers;
  });

  // ── initializeMap ──────────────────────────────────────────────────────

  const initializeMap = useCallback(
    (mapbox: mapboxgl.Map) => {
      if (!mapbox) {
        return { mapbox: undefined, subscription: undefined };
      }

      mapbox.on("move", clearHoverHighlightingDebounced);

      const moveend$ = fromEvent(mapbox, "moveend");
      const movestart$ = fromEvent(mapbox, "movestart");
      const zoomstart$ = fromEvent(mapbox, "zoomstart");
      const zoomend$ = fromEvent(mapbox, "zoomend");
      const zoom$ = fromEvent(mapbox, "zoom");
      const viewLockCancel$ = fromEvent(mapbox, "viewLockCancel");
      const subscription = merge(
        moveend$,
        movestart$,
        zoomstart$,
        zoomend$,
        zoom$,
        viewLockCancel$
      )
        .pipe(
          maprx((i) => i),
          debounceTime(MOVE_DEBOUNCE_MS)
        )
        .subscribe(async () => {
          try {
            if (isMountedRef.current) {
              mapDispatch({
                payload: {
                  bounds: mapbox.getBounds()
                }
              });
            }
            // Composing a screenshot resizes the canvas, which fires moveend; with
            // quantile bins the sync is skipped so a resize can't be mistaken for a
            // user pan and re-derive the boundaries the legend is showing over a
            // different well set. Narrow on purpose: the expanded map and the
            // geom-bin panel also present the map expanded, and freezing them left
            // an unlock unable to bring the hidden wells back.
            if (
              viewLockedRef.current ||
              viewLockedByDashboardRef.current ||
              extentSyncFrozenRef.current
            ) {
              return;
            }
            onMapMove(mapbox);
          } catch (err) {
            Sentry.withScope((scope) => {
              scope.setContext("map_movement", {
                action: "onMapMove",
                viewLocked: viewLockedRef.current,
                dashboardLocked: viewLockedByDashboardRef.current,
                extentSyncFrozen: extentSyncFrozenRef.current
              });
              scope.setLevel("error");
              Sentry.captureException(err);
            });
          }
        });
      if (!viewLockedByDashboardRef.current) {
        onMapMove(mapbox);
      }
      return { mapbox, subscription };
    },
    [
      mapDispatch,
      onMapMove,
      clearHoverHighlightingDebounced,
      isMountedRef,
      viewLockedRef,
      viewLockedByDashboardRef,
      extentSyncFrozenRef
    ]
  );

  // ── Debounce cancel on unmount ─────────────────────────────────────────

  useEffect(() => {
    return () => {
      onMouseLeaveDebounce?.cancel();
      onMouseMoveDebounce?.cancel();
    };
  }, [onMouseMoveDebounce, onMouseLeaveDebounce]);

  // ── Main initialization mount/unmount ──────────────────────────────────

  useEffect(() => {
    if (!isDefaultMapSettingsLoaded) {
      return;
    }

    const mapbox = createMapbox();
    if (isMountedRef.current) {
      mapDispatch({
        payload: {
          isStyleLoaded: false,
          isMapInitialized: false
        }
      });
      mapDispatch({
        payload: {
          mapbox: mapbox,
          baseLayer: defaultBaseLayer ?? "Street"
        }
      });
    }
    mapboxRef.current = mapbox;
    const onStyleLoaded = () => {
      {
        if (isMountedRef.current) {
          mapDispatch({
            payload: {
              isStyleLoaded: true,
              isMapInitialized: false
            }
          });
        }
        const waiting = () => {
          if (!mapbox.isStyleLoaded()) {
            setTimeout(waiting, 200);
          } else {
            loadImages(mapbox);
            addMapboxLayersRef.current();
            if (txnIdRef.current) {
              setTimeout(() => {
                tileUriRef.current = {
                  hz: "",
                  v: "",
                  facility: "",
                  hzRaster: ""
                };
                const {
                  filterWellOnMap: filterWell,
                  updateWellsAndFacilitiesOnMap: updateWellsAndFacilities,
                  canFilterWellsOnMap: canFilterWells,
                  updateLabels: updateMapLabels,
                  entitiesInGroup: currentEntitiesInGroup,
                  facilitiesInGroup: currentFacilitiesInGroup
                } = styleReloadRef.current;
                if (canFilterWells(mapbox)) {
                  filterWell(
                    txnIdRef.current,
                    currentEntitiesInGroup,
                    currentFacilitiesInGroup,
                    checkedPadScenariosRef.current
                  );
                } else {
                  updateWellsAndFacilities(
                    txnIdRef.current.id,
                    txnIdRef.current.facilityId,
                    checkedPadScenariosRef.current,
                    currentFacilitiesInGroup
                  );
                }
                updateMapLabels(txnIdRef.current.facilityId, FACILITY_LABEL_LAYER);
                updateMapLabels(txnIdRef.current.id, WELL_LABEL_LAYER);
                if (isMountedRef.current) {
                  mapDispatch({
                    payload: {
                      isMapInitialized: true
                    }
                  });
                }
              }, 1000);
            }
          }
        };
        waiting();
      }
    };
    mapbox.on("style.load", onStyleLoaded);

    const { subscription: mapSubscription } = initializeMap(mapbox);
    if (isMountedRef.current) {
      mapDispatch({
        payload: {
          created: true
        }
      });
    }

    const clearSelectedData = () => {
      if (selectedGroupsRef.current?.length > 0) {
        return;
      }
      if (mapbox?.getSource(SELECTED_WELL_LAYER)) {
        (mapbox.getSource(SELECTED_WELL_LAYER) as GeoJSONSource)?.setData({
          ...emptyFeatureCollection
        });
        (mapbox.getSource(SELECTED_WELL_POINT_LAYER) as GeoJSONSource)?.setData({
          ...emptyFeatureCollection
        });
      }
    };

    const styleDataHandler = () => {
      if (state.current.extentChanged) {
        state.current.extentChanged = false;
        state.current.styledata = true;
      }
    };
    // Where the camera is, said as mapbox says it. These two events bracket
    // every camera change — a drag, a scroll zoom, a fitBounds flight — and
    // they are the only honest answer to "has the view settled yet", which is
    // what a consumer waiting to read the viewport needs. The move pipeline
    // above cannot answer it: it is debounced by MOVE_DEBOUNCE_MS, so it reports a
    // movement long after the camera has already stopped.
    //
    // The styledata handshake the internal `state.current.isIdle` waits on is
    // deliberately not mirrored here. That flag means "the style finished
    // reloading and the map is quiet"; this one means "the camera is not
    // moving", and a consumer reading bounds does not need tiles to be drawn.
    //
    // The store counts moving maps rather than holding one true/false, so this
    // map has to claim and release exactly one count. Mapbox does not promise
    // the two events alternate — a repeated movestart, or a moveend for a move
    // this map never announced — so the claim is tracked here and each event
    // only acts when it changes what this map is claiming.
    const moveStartHandler = () => {
      if (isClaimingMove.current) return;
      isClaimingMove.current = true;
      beginMapMove();
    };
    const moveEndHandler = () => {
      if (!isClaimingMove.current) return;
      isClaimingMove.current = false;
      endMapMove();
    };

    const idleHandler = () => {
      const today = new Date();
      if (state.current.styledata) {
        state.current.isIdle = true;
      }
      dispatch(
        setMapIdleTime(
          today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds()
        )
      );
    };

    const moveHandler = _debounce((data) => {
      const center = data.target.getCenter();
      const zoom = data.target.getZoom();
      const newMapState = {
        lng: center.lng,
        lat: center.lat,
        zoom: zoom
      };
      try {
        setMapState(newMapState);
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setContext("map_state_update", {
            mapState: newMapState,
            action: "setMapState",
            status: "failed"
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });
      }
    }, 100);

    const errorHandler = (e) => {
      // The map carries a second `error` listener registered at creation, so
      // both see every failed tile. Same shared guard, and the throttle in it
      // collapses the duplicate report.
      if (handleBaseWellTileError(e)) return;
      Sentry.withScope((scope) => {
        scope.setContext("map_general_error", {
          errorType: e?.error?.name,
          errorMessage: e?.error?.message,
          action: "map_error_event",
          status: "failed"
        });
        scope.setLevel("error");
        Sentry.captureException(e?.error || e);
      });
    };

    mapbox.on("zoomstart", clearSelectedData);
    mapbox.on("movestart", clearSelectedData);
    mapbox.on("movestart", moveStartHandler);
    mapbox.on("moveend", moveEndHandler);
    mapbox.on("styledata", styleDataHandler);
    mapbox.on("idle", idleHandler);
    mapbox.on("move", moveHandler);
    mapbox.on("error", errorHandler);

    return () => {
      mapDispatch({
        payload: {
          isMapInitialized: false
        }
      });

      if (mapSubscription) {
        mapSubscription.unsubscribe();
      }

      moveHandler.cancel();
      clearHoverHighlightingDebounced.cancel();

      mapbox.off("move", clearHoverHighlightingDebounced);
      mapbox.off("zoomstart", clearSelectedData);
      mapbox.off("style.load", onStyleLoaded);
      mapbox.off("movestart", clearSelectedData);
      mapbox.off("movestart", moveStartHandler);
      mapbox.off("moveend", moveEndHandler);
      mapbox.off("dragstart", clearHoverHighlighting);
      mapbox.off("idle", idleHandler);
      mapbox.off("move", moveHandler);
      mapbox.off("styledata", styleDataHandler);
      mapbox.off("error", errorHandler);
      // A map torn down mid-flight would otherwise leave its count claimed
      // with no camera left to release it. Only its own count goes back — a
      // second map still moving keeps saying so.
      if (isClaimingMove.current) {
        isClaimingMove.current = false;
        endMapMove();
      }
      mapboxRef.current = undefined;
      setMapInstance(null);
      mapbox.remove();
    };
  }, [isDefaultMapSettingsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
}
